/*
 * The master inbox's open conversation, drawn as the reply screens draw theirs (master-inbox plan,
 * step 4: docs/2026-10-17-master-inbox-in-the-existing-ui-plan.md). Paper ground, never a white pane
 * (D3); the incumbent head: the person and company, a meta line with the campaign as a bronze link and
 * the thread's mailbox, and the actions as incumbent buttons with keycaps on a desktop. Messages are the
 * incumbent cards with their From/To/CC/Subject fields and the shared email frame (`mail/mail-frame.tsx`).
 * The thread reads newest first, every message drawn whole, with no fold (LEA-514; Dawid,
 * 2026-11-04, docs/2026-11-04-thread-newest-first-design.md). The DOM is in that order too, so Tab
 * and a screen reader meet the messages as the eye does. No label change sits between emails: who
 * set the status opens its change log from the head, and each lead message carries what the AI read
 * in it (`label-history.tsx`).
 *
 * Under the reply box, above the newest message, there is one mount point, `afterNewest`, where
 * step 8 puts the agent's material as a timeline item. Nothing here asks a reply-agent route.
 */
import {
  Fragment,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { appDateTime, appDayMonth, appWeekdayTime } from "../../src/app-dates.ts";
import type { InboxLabelCode } from "../../src/inbox-label.ts";
import type { InboxMessage, InboxThread } from "../../src/inbox-v2/contract.ts";
import { rowSubject } from "../../src/inbox-v2/row-subject.ts";
import { SHOW_MESSAGE_EVENT } from "../agent/conversation-shared.ts";
import {
  AttachmentIcon,
  BackIcon,
  CalendarIcon,
  CheckIcon,
  ChevronDownIcon,
  ClockIcon,
  ConfirmIcon,
  ExternalLinkIcon,
  HistoryIcon,
  MoreIcon,
  PersonIcon,
  RestoreIcon,
  SeenIcon,
  TrashIcon,
} from "../icons.tsx";
import { FrameSettleProvider, usePaneSettle, usePaneSettled } from "../mail/frame-settle.ts";
import { MailFrame } from "../mail/mail-frame.tsx";
import { prepareMailDisplay } from "../mail/mail-images.ts";
import { type MenuRow, OptionMenu } from "../option-menu.tsx";
import { Tip } from "../tip.tsx";
import { useFold } from "../use-fold.ts";
import {
  type Assignment,
  assigneeWord,
  assignOptions,
  PeopleNote,
  UNASSIGNED,
} from "./assignment.tsx";
import { HandoverMeta } from "./handover-meta.tsx";
import {
  type AiReading,
  aiReading,
  ChangeLog,
  headStatus,
  IntentTag,
  meetingNote,
} from "./label-history.tsx";
import { DataRequestMarker, LabelDot } from "./label-key.tsx";
import { LABEL_NAMES, RELABEL_CHOICES } from "./labels.ts";
import { personName } from "./list.tsx";
import { MeetingRequestMarker } from "./meeting-marker.tsx";
import { ForwardHistory, MessageActions } from "./message-actions.tsx";
import { useOutgoingReply } from "./outgoing-reply.ts";
import { useBoxes } from "./thread-boxes.tsx";
import { useReplySend } from "./use-reply-send.ts";
import { useViewHeld } from "./use-view-held.ts";

// Resolved at the first parse, not at import: a module loaded before a DOM exists would otherwise
// keep a null parser for good (a test process that loads it early, as a CI shard did).
let parser: DOMParser | null = null;
const parseEmail = (html: string): Document => {
  if (!parser && typeof DOMParser !== "undefined") parser = new DOMParser();
  if (!parser) throw new Error("Email display needs a DOM");
  return parser.parseFromString(html, "text/html");
};

const labelName = (code: InboxLabelCode | null) => (code === null ? "No label" : LABEL_NAMES[code]);

/** The message a folded quote copies, as the fold names it: "our email of 12 Oct". */
function quotedSource(earlier: readonly InboxMessage[], ids: readonly string[]) {
  const message = earlier.find((candidate) => candidate.id === ids[0]);
  if (!message) return null;
  const whose = message.direction === "outbound" ? "our" : `${message.from}'s`;
  return { id: message.id, words: `${whose} email of ${appDayMonth(message.at)}` };
}

/** How a person is named in the header's one line: their name, else the address before the @. */
function shortName(address: string): string {
  const named = /^\s*"?([^"<]+?)"?\s*<[^>]+>\s*$/u.exec(address)?.[1];
  return named ?? address.split("@")[0] ?? address;
}

/** "to maya, cc ops": empty where neither was recorded. */
function recipientLine(to: readonly string[], cc: readonly string[]): string {
  return [
    to.length > 0 ? `to ${to.map(shortName).join(", ")}` : "",
    cc.length > 0 ? `cc ${cc.map(shortName).join(", ")}` : "",
  ]
    .filter(Boolean)
    .join(", ");
}

/** A subject without its reply and forward prefixes, for telling a new subject from the thread's. */
const bareSubject = (subject: string) =>
  subject
    .replace(/^\s*(?:(?:re|fwd?|fw|aw|sv|tr)\s*:\s*)+/iu, "")
    .replace(/\s+/gu, " ")
    .trim()
    .toLowerCase();

/**
 * Who a message went to, as mail clients say it: one quiet line under the sender, "to maya, cc ops",
 * that opens to the full header. Its subject shows only where it is not the thread's own. A field
 * the provider did not record is left out, never shown blank; the sender is the head's, so it is not
 * said again (design review #18).
 */
function MessageHeader(props: { message: InboxMessage; threadSubject: string }) {
  const { message } = props;
  const [open, setOpen] = useState(false);
  const id = useId();
  const own = bareSubject(message.subject) !== bareSubject(props.threadSubject);
  const line = recipientLine(message.to, message.cc);
  return (
    <>
      <button
        type="button"
        className="lcr-i2-mail-recipients"
        aria-label={`${line || "no recipients on file"}, ${open ? "hide" : "show"} details`}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen(!open)}
      >
        <span className="lcr-i2-mail-recipients-line">{line || "no recipients on file"}</span>
        <ChevronDownIcon size={14} />
      </button>
      {own && !open && <p className="lcr-i2-mail-subject">{message.subject}</p>}
      {open && (
        <dl className="lcr-email-fields" id={id}>
          {message.to.length > 0 && (
            <>
              <dt>To</dt>
              <dd>{message.to.join(", ")}</dd>
            </>
          )}
          {message.cc.length > 0 && (
            <>
              <dt>CC</dt>
              <dd>{message.cc.join(", ")}</dd>
            </>
          )}
          <dt>Subject</dt>
          <dd>{message.subject}</dd>
        </dl>
      )}
    </>
  );
}

/** A message as a full incumbent card: who, when, the fields, the email and its files. */
function MessageCard(props: {
  message: InboxMessage;
  reading: AiReading | null;
  /** The messages before this one, oldest first: what its quoted history is matched against. */
  earlier: readonly InboxMessage[];
  workspaceId: string;
  planId: string;
  attachmentHref: (id: string) => string;
  forwardEnabled?: boolean;
  forwards?: InboxThread["forwards"];
  replyAction?: (message: InboxMessage) => ReactNode;
  threadSubject: string;
}) {
  const { message } = props;
  const display = useMemo(
    () => prepareMailDisplay(message.html, props.earlier, parseEmail),
    [message.html, props.earlier],
  );
  return (
    <article
      className={`lcr-message lcr-i2-message${message.direction === "inbound" ? " lcr-received" : ""}`}
      id={`message-${message.id}`}
      data-side={message.direction}
      // A link to the message (`useShowMessage`) puts the focus here, not on a control inside it.
      tabIndex={-1}
    >
      <div className="lcr-mail-head">
        {/* A heading, so a screen reader can move from message to message (design review #15). */}
        <h3 className="lcr-mail-from">{message.from}</h3>
        <IntentTag reading={props.reading} />
        <time dateTime={message.at}>{appDateTime(message.at)}</time>
        <MessageActions
          workspaceId={props.workspaceId}
          planId={props.planId}
          messageId={message.id}
          enabled={props.forwardEnabled === true}
          reply={props.replyAction?.(message)}
        />
      </div>
      <MessageHeader message={message} threadSubject={props.threadSubject} />
      <MailFrame
        display={display}
        label={`Email from ${message.from}`}
        images={{ workspaceId: props.workspaceId, planId: props.planId, messageId: message.id }}
        attachments={message.attachments}
        attachmentHref={props.attachmentHref}
        quotedSource={(ids) => quotedSource(props.earlier, ids)}
      />
      {message.attachments.length > 0 && (
        <ul className="lcr-attachments" aria-label="Attachments">
          {message.attachments.map((file) => (
            <li key={file.id}>
              <a href={props.attachmentHref(file.id)} download={file.name}>
                <AttachmentIcon size={14} />
                <span className="lcr-attachment-name">{file.name}</span>
              </a>
            </li>
          ))}
        </ul>
      )}
      <ForwardHistory messageId={message.id} history={props.forwards} />
    </article>
  );
}

/**
 * The rep's reply on its way, after its hold (`outgoing-reply.ts`): our side's card with the words
 * that went, its state where a message's date stands, "Sending…" and then "Sent", until Bison's copy
 * is in the thread and takes its place. Nothing is drawn where this browser did not send it.
 */
function OutgoingMessage(props: { workspaceId: string; thread: InboxThread }) {
  const outgoing = useOutgoingReply(props.workspaceId, props.thread);
  const card = useRef<HTMLElement>(null);
  // It appears as the hold ends, under whatever the rep was reading, and comes into view once; a
  // thread opened while it is already there keeps its own opening place.
  const shown = outgoing?.reply.id ?? null;
  const drawn = useRef(shown);
  useEffect(() => {
    if (shown && shown !== drawn.current) card.current?.scrollIntoView?.({ block: "nearest" });
    drawn.current = shown;
  }, [shown]);
  if (!outgoing) return null;
  const { reply, state } = outgoing;
  const line = recipientLine(reply.to, reply.cc);
  return (
    <li>
      <article
        ref={card}
        className="lcr-message lcr-i2-message lcr-i2-outgoing"
        data-side="outbound"
        data-state={state}
      >
        <div className="lcr-mail-head">
          <h3 className="lcr-mail-from">{reply.from ?? "Your reply"}</h3>
          <p className="lcr-i2-outgoing-state" role="status">
            {state === "sent" && <CheckIcon size={14} />}
            {state === "sent" ? "Sent" : "Sending…"}
          </p>
        </div>
        {line && <p className="lcr-i2-outgoing-to">{line}</p>}
        <p className="lcr-i2-outgoing-words">{reply.plainText}</p>
      </article>
    </li>
  );
}

/**
 * A link to one message (an address's `#message-<id>`, Activity's "Show message", a source chip's
 * anchor) scrolls to the card and puts the focus on it, so no link lands on nothing. The address is
 * read once, as the thread opens; a later read of the same thread does not move the reader back to it.
 */
function useShowMessage(messages: readonly InboxMessage[]): void {
  const paneSettled = usePaneSettled();
  const [reveal, setReveal] = useState<string | null>(null);
  // The listeners stay for the thread's life; what they show is always this render's thread.
  const show = useRef<(id: string) => void>(() => undefined);
  show.current = (id: string) => {
    if (!messages.some((message) => message.id === id)) return;
    setReveal(id);
  };
  useEffect(() => {
    const fromHash = (): void => {
      const match = /^#message-(.+)$/.exec(window.location.hash);
      if (match?.[1]) show.current(decodeURIComponent(match[1]));
    };
    const fromActivity = (event: Event): void => {
      if (event instanceof CustomEvent && typeof event.detail === "string")
        show.current(event.detail);
    };
    fromHash();
    window.addEventListener("hashchange", fromHash);
    window.addEventListener(SHOW_MESSAGE_EVENT, fromActivity);
    return () => {
      window.removeEventListener("hashchange", fromHash);
      window.removeEventListener(SHOW_MESSAGE_EVENT, fromActivity);
    };
  }, []);
  // The card is drawn, but its pane may still be hidden while emails are measured.
  useEffect(() => {
    if (reveal === null || !paneSettled) return;
    const target = document.getElementById(`message-${reveal}`);
    if (!(target instanceof HTMLElement) || target.tagName !== "ARTICLE") return;
    // Finish after the fragment navigation's own focus reset, not inside its hash-change task.
    const frame = window.requestAnimationFrame(() => {
      if (!target.isConnected) return;
      target.scrollIntoView?.({ block: "start" });
      target.focus({ preventScroll: true });
      setReveal((current) => (current === reveal ? null : current));
    });
    return () => window.cancelAnimationFrame(frame);
  });
}

type SentBounce = NonNullable<InboxThread["bounces"]>[number];
const BOUNCE_WORDS: Record<SentBounce["kind"], string> = {
  hard: "Hard bounce: the address does not take mail",
  soft: "Soft bounce: the receiving server turned it away for now",
  unplaced: "Bison returned it undelivered",
};

/**
 * One of our own sent answers bounced (LEA-311): said on the thread, read-only. What follows a
 * bounce is Bison's, so the way on is Bison's page for the notice.
 */
function BouncedMarker(props: { bounce: SentBounce }) {
  const { bounce } = props;
  return (
    <p className="lcr-bounce-marker" role="note">
      <span className="lcr-bounce-marker-pill">Bounced</span>
      <span className="lcr-bounce-marker-words">
        {BOUNCE_WORDS[bounce.kind]} · {appDateTime(bounce.at)}
      </span>
      {bounce.bisonUrl && (
        <a className="lcr-linklike" href={bounce.bisonUrl} target="_blank" rel="noreferrer">
          Open in Bison
        </a>
      )}
    </p>
  );
}

/**
 * A message's boxes, an item of their own above its card, so the card's item can hold its place as
 * one opens (`use-view-held.ts`); nothing is drawn while it has none.
 */
function BoxSlot(props: { message: InboxMessage; draw: (message: InboxMessage) => ReactNode }) {
  const boxes = useBoxes();
  if (!boxes?.above(props.message.id).length) return null;
  return <li className="lcr-i2-box-slot">{props.draw(props.message)}</li>;
}

/**
 * The thread, newest first (LEA-514): the order is reversed here and nowhere else, so
 * `thread.messages` stays oldest first for everything that reads it (quote matching, audiences,
 * `ThreadBelow`). Each card's `earlier` is still the messages before it in time.
 */
function Messages(props: {
  thread: InboxThread;
  workspaceId: string;
  attachmentHref: (id: string) => string;
  forwardEnabled?: boolean;
  forwards?: InboxThread["forwards"];
  replyAction?: (message: InboxMessage) => ReactNode;
  /** The boxes opened above a message's card (`thread-boxes.tsx`). */
  boxAbove?: (message: InboxMessage) => ReactNode;
}) {
  const { messages } = props.thread;
  // A box whose message a thread read left out heads the list, under its message's own key, so it
  // moves there without being drawn again and its words and focus stay (LEA-514).
  const orphans = [
    ...new Map((useBoxes()?.orphans ?? []).map((box) => [box.message.id, box.message])).values(),
  ];
  const bounces = props.thread.bounces ?? [];
  // A sent answer Bison has not put in the thread yet keeps its bounce with the newest messages.
  const unplaced = bounces.filter(
    (bounce) => !messages.some((message) => message.id === bounce.messageId),
  );
  const planId = props.thread.conversation.id;
  useShowMessage(messages);
  const newestFirst = useMemo(
    () => messages.map((message, index) => ({ message, index })).reverse(),
    [messages],
  );
  const draw = props.boxAbove;
  // One keyed list: a message's entry keeps its key wherever it stands, so React moves it.
  const items: ReactNode[] = [
    ...(draw
      ? orphans.map((message) => (
          <Fragment key={message.id}>
            <BoxSlot message={message} draw={draw} />
          </Fragment>
        ))
      : []),
    <OutgoingMessage key="outgoing" workspaceId={props.workspaceId} thread={props.thread} />,
    ...unplaced.map((bounce) => (
      <li key={`${bounce.messageId}:${bounce.at}`}>
        <BouncedMarker bounce={bounce} />
      </li>
    )),
    ...newestFirst.map(({ message, index }) => (
      <Fragment key={message.id}>
        {draw && <BoxSlot message={message} draw={draw} />}
        <li>
          <MessageCard
            message={message}
            reading={aiReading(message, props.thread.changes)}
            earlier={messages.slice(0, index)}
            workspaceId={props.workspaceId}
            planId={planId}
            attachmentHref={props.attachmentHref}
            forwardEnabled={props.forwardEnabled === true}
            forwards={props.thread.forwards}
            threadSubject={props.thread.conversation.subject}
            {...(props.replyAction ? { replyAction: props.replyAction } : {})}
          />
          {bounces
            .filter((bounce) => bounce.messageId === message.id)
            .map((bounce) => (
              <BouncedMarker key={`${bounce.messageId}:${bounce.at}`} bounce={bounce} />
            ))}
        </li>
      </Fragment>
    )),
  ];
  return <ol className="lcr-mail-thread lcr-i2-messages">{items}</ol>;
}

/*
 * The head's label menu: each label with its key's dot, as the rail and the rows draw it, and on a
 * desktop its number, 1 to 8, which chooses it while the menu is open. "No label" has a ring, no key.
 */
export const RELABEL_OPTIONS = [...RELABEL_CHOICES, "none" as const].map((value, index) => ({
  value,
  label: value === "none" ? "No label" : LABEL_NAMES[value],
  mark: <LabelDot code={value === "none" ? null : value} />,
  ...(value === "none" ? {} : { shortcut: String(index + 1) }),
}));

export type ThreadActions = {
  relabelOpen: boolean;
  onRelabelOpen: (open: boolean) => void;
  onRelabel: (label: InboxLabelCode | null) => void;
  onConfirm: () => void;
  onDone: () => void;
  onTrash: (() => void) | null;
  inTrash: boolean;
  onSeen: (seen: boolean) => void;
  /** Opens Snooze's choices under the head's own button (the inbox's one Snooze, `snooze-menu.tsx`). */
  onSnoozeOpen: (() => void) | null;
  /** Snooze's choices while they are open here, else null. */
  snoozeMenu: ReactNode;
  /** Who it is assigned to, and the menu that changes it; null where assignment is off. */
  assign: AssignControl | null;
  /** More's menu, open from outside too (the "." key). */
  moreOpen: boolean;
  onMoreOpen: (open: boolean) => void;
  /** The rep's word on the meeting flag (LEA-494); null where the head offers none. */
  onMeeting: ((meeting: boolean) => void) | null;
};

/** The head's Assign: the thread's assignee as the menu's value, and the people to choose from. */
export type AssignControl = {
  readonly assignment: Assignment;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onChoose: (value: string) => void;
};

/*
 * How far the head's toolbar has folded to fit its one row, roomiest first. The row never wraps and
 * never shares a line with the subject: where its actions do not fit, the next fold is taken until
 * they do. Measured, not set by breakpoints, because what the row holds changes per conversation
 * (Confirm only when the AI was unsure, Assign only where assignment is on, a label's or a person's
 * name as long as it is). Before this, the row sat beside the subject in a grid's `auto` column and
 * took its whole width first, so between an 820 and a ~1150px pane the subject was crushed to a
 * column a letter wide (critique of 2026-11-01).
 */
const FOLD = {
  /** Keycaps leave the buttons for tips on hover and keyboard focus. */
  tips: 1,
  /** Confirm shows its mark alone. */
  confirmMark: 2,
  /** Assign shows its person mark alone. */
  assignMark: 3,
  /** Assign joins More, as a group of the same choices. */
  assign: 4,
  /** Confirm joins More. Label, Done and More always stay. */
  confirm: 5,
  /**
   * Done, the action used most, is the last to lose its word, and only once Confirm has left the
   * row: a lone check beside an unsure label read as "confirm it" and archived the thread instead.
   */
  doneMark: 6,
} as const;
type Fold = number;

/** Where each key moves the toolbar's focus from stop `at` of `count`, wrapping at either end. */
const ROVE: Readonly<Record<string, (at: number, count: number) => number>> = {
  ArrowRight: (at, count) => (at + 1) % count,
  ArrowLeft: (at, count) => (at - 1 + count) % count,
  Home: () => 0,
  End: (_at, count) => count - 1,
};

/** The toolbar's stops: each action's own button, menus' included. */
const STOPS = ".lcr-i2-head-action > button, .lcr-i2-head-action > .lcr-option-menu > button";

/**
 * One Tab stop for the whole toolbar, arrows between its actions (WAI-ARIA APG, Toolbar), as the
 * selection bar's. The stop is the last action used, so Tab comes back to where the reader was.
 * Arrows inside an open menu or Snooze's choices are theirs: only a key on a stop moves here.
 */
function useRoving(bar: React.RefObject<HTMLDivElement | null>) {
  const active = useRef(0);
  const stops = (): HTMLElement[] => [...(bar.current?.querySelectorAll<HTMLElement>(STOPS) ?? [])];
  const settle = (items: readonly HTMLElement[]) => {
    for (const [index, item] of items.entries()) item.tabIndex = index === active.current ? 0 : -1;
  };
  useLayoutEffect(() => {
    const items = stops();
    if (active.current >= items.length) active.current = 0;
    settle(items);
  });
  return {
    onFocus: (event: React.FocusEvent<HTMLDivElement>) => {
      const items = stops();
      const index = event.target instanceof HTMLElement ? items.indexOf(event.target) : -1;
      if (index < 0 || index === active.current) return;
      active.current = index;
      settle(items);
    },
    onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
      const items = stops();
      const index = event.target instanceof HTMLElement ? items.indexOf(event.target) : -1;
      const next = index < 0 ? undefined : ROVE[event.key]?.(index, items.length);
      if (next === undefined) return;
      event.preventDefault();
      event.stopPropagation();
      active.current = next;
      settle(items);
      items[next]?.focus();
    },
  };
}

/** Where an action's key is drawn: a keycap on its button, a tip, or nowhere (phone, touch). */
/** A keycap on the button, a tip with the word and its key, a tip with the word alone, or nothing. */
type KeyHint = "cap" | "tip" | "word" | null;

/**
 * One of the head's actions with its key: a keycap on the button while the row has room for them,
 * else a tip that says the word and the key on hover and on keyboard focus (Dawid, 2026-10-17,
 * "Keycaps into tooltips"). Neither where keys are not shown at all: a phone or a touch screen.
 */
function HeadAction(props: {
  word: string;
  keyName: string;
  hint: KeyHint;
  className?: string;
  /** Shows its mark alone, its word kept for a screen reader (a fold, or a phone). */
  marked?: boolean;
  children: (keycap: ReactNode) => ReactNode;
}) {
  // The button names its key through `aria-keyshortcuts`, so its keycap is drawn, not read: a span,
  // as the search field's `/` is.
  const keycap =
    props.hint === "cap" ? (
      <span className="lcr-kbd lcr-i2-head-key" aria-hidden="true">
        {props.keyName}
      </span>
    ) : null;
  return (
    <span
      className={`lcr-i2-head-action${props.className ? ` ${props.className}` : ""}${props.marked ? " lcr-i2-head-marked" : ""}`}
    >
      {props.children(keycap)}
      {(props.hint === "tip" || props.hint === "word") && (
        <Tip className="lcr-i2-head-tip">
          {props.word}
          {props.hint === "tip" && <kbd className="lcr-kbd">{props.keyName}</kbd>}
        </Tip>
      )}
    </span>
  );
}

/** The head's Assign menu: the assignee as its value ("You", a name, or Unassigned). */
function HeadAssign(props: {
  thread: InboxThread;
  control: AssignControl;
  hint: KeyHint;
  marked: boolean;
  showKeys: boolean;
}) {
  const { control } = props;
  const assignee = props.thread.assignment?.assignee ?? null;
  return (
    <HeadAction word="Assign" keyName="M" hint={props.hint} marked={props.marked}>
      {(keycap) => (
        <OptionMenu
          label="Assigned to"
          className="lcr-i2-head-menu lcr-i2-assign"
          icon={<PersonIcon size={14} />}
          value={assignee?.userId ?? UNASSIGNED}
          display={assignee ? assigneeWord(assignee, control.assignment.you) : "Unassigned"}
          options={assignOptions(control.assignment)}
          onChange={control.onChoose}
          keycap={keycap}
          {...(props.showKeys ? { keyshortcuts: "M" } : {})}
          showKeys={props.showKeys}
          open={control.open}
          onOpenChange={control.onOpenChange}
          after={<PeopleNote loading={control.assignment.loading} />}
        />
      )}
    </HeadAction>
  );
}

/** What More's rows choose; an assignee is `assign:` and the person's id. */
const ASSIGN = "assign:";

/**
 * More: what is used less, or would not fit. Snooze, Mark seen or unseen and Delete always live here,
 * Delete last, so the only destructive action is never a pill beside Done (owner's choice of
 * 2026-11-01: Label, Confirm, Assign and Done stay in the row). Assign and Confirm join it only when
 * the row is too narrow for them. Each row keeps its key, which chooses it while the menu is open.
 */
/** More's Assign: the same choices as the row's menu, one level down, the current one said in words. */
function assignGroup(assign: AssignControl, thread: InboxThread): MenuRow<string> {
  const assignee = thread.assignment?.assignee ?? null;
  const current = assignee?.userId ?? UNASSIGNED;
  return {
    key: "assign",
    label: "Assign",
    detail: assignee ? assigneeWord(assignee, assign.assignment.you) : "Unassigned",
    mark: <PersonIcon size={16} />,
    value: `${ASSIGN}${current}`,
    open: assign.open,
    // A menu of commands ticks nothing, so the current choice says so in words.
    submenu: assignOptions(assign.assignment).map((option) => ({
      ...option,
      value: `${ASSIGN}${option.value}`,
      ...(option.value === current ? { detail: "Current", current: true } : {}),
    })),
  };
}

/**
 * More's meeting row (LEA-494): the rep's word on whether the newest reply asks to meet, which wins
 * over the AI's. Only where the thread's read carries the flag and has a lead message to name. A yes
 * on another label also labels it Interested, and the row says so before it is chosen.
 */
function meetingRow(thread: InboxThread, actions: ThreadActions): MenuRow<string>[] {
  const row = thread.conversation;
  if (!actions.onMeeting || !row.leadMessageId || row.meetingRequest === undefined) return [];
  if (row.meetingRequest)
    return [{ value: "meeting", label: "Not a meeting request", mark: <CalendarIcon size={16} /> }];
  return [
    {
      value: "meeting",
      label: "Mark as meeting request",
      ...(row.label?.code === "interested" ? {} : { detail: "Also labels it Interested" }),
      mark: <CalendarIcon size={16} />,
    },
  ];
}

/** More's rows that are always there: Snooze where it is offered, Mark seen or unseen, Delete last. */
function standingRows(thread: InboxThread, actions: ThreadActions): MenuRow<string>[] {
  const snoozed = snoozedUntil(thread);
  const restore = actions.inTrash;
  const snooze: MenuRow<string> = {
    value: "snooze",
    label: snoozed ? "Change snooze" : "Snooze",
    ...(snoozed ? { detail: `Snoozed until ${appWeekdayTime(snoozed)}` } : {}),
    mark: <ClockIcon size={16} />,
    shortcut: "H",
  };
  // Its own class draws a hairline above the row: the one destructive choice stands apart.
  const trashMark = restore ? <RestoreIcon size={16} /> : <TrashIcon size={16} />;
  const trash: MenuRow<string> = {
    value: "trash",
    label: restore ? "Restore from Trash" : "Delete",
    mark: <span className="lcr-i2-more-trash">{trashMark}</span>,
    shortcut: "#",
  };
  return [
    ...(actions.onSnoozeOpen ? [snooze] : []),
    {
      value: "seen",
      label: thread.conversation.unread ? "Mark seen" : "Mark unseen",
      mark: <SeenIcon size={16} />,
      shortcut: "U",
    },
    ...(actions.onTrash ? [trash] : []),
  ];
}

/** What a row of More does: an assignee is `assign:` and the person's id. */
function chooseMore(value: string, actions: ThreadActions, row: InboxThread["conversation"]): void {
  if (value.startsWith(ASSIGN)) actions.assign?.onChoose(value.slice(ASSIGN.length));
  else if (value === "meeting") actions.onMeeting?.(row.meetingRequest !== true);
  else if (value === "confirm") actions.onConfirm();
  else if (value === "snooze") actions.onSnoozeOpen?.();
  else if (value === "seen") actions.onSeen(row.unread);
  else if (value === "trash") actions.onTrash?.();
}

function HeadMore(props: {
  thread: InboxThread;
  actions: ThreadActions;
  fold: Fold;
  hint: KeyHint;
  showKeys: boolean;
}) {
  const { actions, fold, thread } = props;
  const row = thread.conversation;
  const assign = fold >= FOLD.assign && thread.assignment ? actions.assign : null;
  const confirm: MenuRow<string> = {
    value: "confirm",
    label: "Confirm the label",
    detail: labelName(row.label?.code ?? null),
    mark: <ConfirmIcon size={16} />,
    shortcut: "C",
  };
  const rows: MenuRow<string>[] = [
    ...(fold >= FOLD.confirm && row.label?.uncertain === true ? [confirm] : []),
    ...(assign ? [assignGroup(assign, thread)] : []),
    ...meetingRow(thread, actions),
    ...standingRows(thread, actions),
  ];
  const choose = (value: string) => chooseMore(value, actions, row);
  return (
    <HeadAction word="More" keyName="." hint={props.hint} className="lcr-i2-head-more">
      {(keycap) => (
        <>
          <OptionMenu
            label="More actions"
            className="lcr-i2-head-menu lcr-i2-head-more-menu"
            icon={<MoreIcon size={16} />}
            value=""
            display={null}
            caret={false}
            actions
            options={rows}
            onChange={choose}
            keycap={keycap}
            {...(props.showKeys ? { keyshortcuts: "." } : {})}
            showKeys={props.showKeys}
            open={actions.moreOpen || (assign?.open ?? false)}
            onOpenChange={(open) => {
              actions.onMoreOpen(open);
              if (!open && assign?.open) assign.onOpenChange(false);
            }}
          />
          {actions.snoozeMenu}
        </>
      )}
    </HeadAction>
  );
}

/**
 * The head's toolbar, one row: the label menu (its value the label), Confirm when the AI was unsure,
 * Assign, Done, and More. Its fold (`useFold`) decides where the keys are drawn and what moves into
 * More; every key works whatever the fold.
 */
function HeadActions(props: {
  thread: InboxThread;
  actions: ThreadActions;
  showKeys: boolean;
  /** Rows are ticked: E, L, M, H, U, # and . act on them (the bar's tips say so), not on this one. */
  selecting: boolean;
}) {
  const row = props.thread.conversation;
  const { actions } = props;
  // While rows are ticked the head claims no key and draws its tips with words alone, so it never
  // advertises a key that would act on the ticked rows instead (critique of 2026-11-04).
  const showKeys = props.showKeys && !props.selecting;
  const code = row.label?.code ?? null;
  const unsure = row.label?.uncertain === true;
  const doneWord = row.doneAt ? "Back to inbox" : "Done";
  const assignee = props.thread.assignment?.assignee?.userId ?? "";
  const bar = useRef<HTMLDivElement>(null);
  const content = [code, unsure, assignee, row.doneAt, snoozedUntil(props.thread), showKeys].join(
    "|",
  );
  const wordsOnly = props.showKeys && props.selecting;
  const fold = useFold(bar, content, FOLD.doneMark);
  const roving = useRoving(bar);
  const hint: KeyHint = wordsOnly ? "word" : !showKeys ? null : fold >= FOLD.tips ? "tip" : "cap";
  // The key each button answers to, for assistive technology; the keycap and the tip are the eye's.
  const keys = (name: string) => (showKeys ? { "aria-keyshortcuts": name } : {});
  return (
    <div
      ref={bar}
      className="lcr-i2-head-actions"
      role="toolbar"
      aria-label="Conversation actions"
      onFocus={roving.onFocus}
      onKeyDown={roving.onKeyDown}
    >
      {row.leadMessageId ? (
        <HeadAction word="Label" keyName="L" hint={hint}>
          {(keycap) => (
            <OptionMenu
              label="Label"
              className="lcr-i2-head-menu lcr-i2-relabel"
              icon={<LabelDot code={code} unsure={unsure} />}
              value={code ?? "none"}
              display={labelName(code)}
              {...(unsure ? { summary: `${labelName(code)}, the AI was not sure` } : {})}
              options={RELABEL_OPTIONS}
              onChange={(value) => actions.onRelabel(value === "none" ? null : value)}
              keycap={keycap}
              {...(showKeys ? { keyshortcuts: "L" } : {})}
              showKeys={showKeys}
              open={actions.relabelOpen}
              onOpenChange={actions.onRelabelOpen}
            />
          )}
        </HeadAction>
      ) : null}
      {unsure && fold < FOLD.confirm && (
        <HeadAction word="Confirm" keyName="C" hint={hint} marked={fold >= FOLD.confirmMark}>
          {(keycap) => (
            <button
              type="button"
              className="lcr-small-button lcr-i2-confirm lcr-i2-head-iconic"
              aria-label={`Confirm the label: ${labelName(code)}`}
              {...keys("C")}
              onClick={actions.onConfirm}
            >
              <ConfirmIcon size={14} />
              <span className="lcr-i2-head-word">Confirm</span>
              {keycap}
            </button>
          )}
        </HeadAction>
      )}
      {actions.assign && props.thread.assignment && fold < FOLD.assign && (
        <HeadAssign
          thread={props.thread}
          control={actions.assign}
          hint={hint}
          marked={fold >= FOLD.assignMark}
          showKeys={showKeys}
        />
      )}
      <HeadAction word={doneWord} keyName="E" hint={hint} marked={fold >= FOLD.doneMark}>
        {(keycap) => (
          <button
            type="button"
            className="lcr-small-button lcr-i2-head-iconic lcr-i2-head-done"
            {...keys("E")}
            onClick={actions.onDone}
          >
            <CheckIcon size={14} />
            <span className="lcr-i2-head-word">{doneWord}</span>
            {keycap}
          </button>
        )}
      </HeadAction>
      <HeadMore
        thread={props.thread}
        actions={actions}
        fold={fold}
        hint={hint}
        showKeys={showKeys}
      />
    </div>
  );
}

/**
 * The campaign as a link that narrows the list to it. Its whole name and what the link does are in a
 * tip under it, not the browser's own `title`, which came late and covered the line below; a name
 * longer than the line has room for is cut short and read whole there.
 */
function HeadCampaign(props: {
  campaign: InboxThread["conversation"]["campaign"];
  onCampaign: ((campaignId: string) => void) | null;
}) {
  const { campaign, onCampaign } = props;
  if (!onCampaign) return <span className="lcr-i2-head-campaign">{campaign.name}</span>;
  return (
    <span className="lcr-i2-head-campaign lcr-i2-meta-tipped">
      <button
        type="button"
        className="lcr-linklike"
        aria-description="Shows this campaign's replies"
        onClick={() => onCampaign(campaign.id)}
      >
        {campaign.name}
      </button>
      <Tip className="lcr-i2-meta-tip">Show the replies to {campaign.name}</Tip>
    </span>
  );
}

/**
 * The line that opens the history under the head: who set the status ("Labelled by the AI, 21 Oct"),
 * or "History" where only the assignment or the teammates who read it are on record; those are in
 * the panel it opens, not on lines of their own. On a narrow pane it is its mark and date, its words
 * in a tip, so the campaign keeps the room.
 */
function HeadHistory(props: {
  thread: InboxThread;
  open: boolean;
  panelId: string;
  onToggle: () => void;
}) {
  const { thread } = props;
  const words = headStatus(thread);
  const seen = (thread.seenBy ?? []).length > 0;
  if (!words && !thread.assignmentRecord && !thread.handoverSnooze && !seen) return null;
  return (
    <span className="lcr-i2-head-history lcr-i2-meta-tipped">
      <button
        type="button"
        className="lcr-linklike lcr-i2-set-by"
        aria-label={words ? `${words.who}, ${words.when}, history` : "History"}
        aria-expanded={props.open}
        aria-controls={props.open ? props.panelId : undefined}
        onClick={props.onToggle}
      >
        <HistoryIcon size={14} />
        {/* One span, so the link's underline runs unbroken across the flex gap. */}
        <span>
          {words ? (
            <>
              <span className="lcr-i2-set-by-who">{words.who}, </span>
              <span className="lcr-i2-set-by-short">{words.short}, </span>
              {words.when}
            </>
          ) : (
            "History"
          )}
        </span>
      </button>
      {words && (
        <Tip className="lcr-i2-meta-tip lcr-i2-narrow-tip">
          {words.who}, {words.when}
        </Tip>
      )}
    </span>
  );
}

/** Snoozed until when: the conversation's own snooze, or the one a handover set. */
function snoozedUntil(thread: InboxThread): string | null {
  return thread.conversation.snooze?.until ?? thread.handoverSnooze?.until ?? null;
}

/**
 * The head, in three rows that never share a line: the toolbar; the subject, as the list row shows it
 * (`rowSubject`, without the campaign's name, which the meta line says), in two lines at most; then
 * one meta line (the lead, the company, the campaign, the mailbox, a snooze, Open in Bison, the
 * history link). Outbound reps know a conversation by its subject, not by a name they have never met.
 * The subject comes first in the markup, so a screen reader reads it before the actions; the
 * stylesheet draws the toolbar above it, and the subject takes no focus, so Tab still runs top down.
 */
function ThreadHead(props: {
  thread: InboxThread;
  actions: ThreadActions;
  showKeys: boolean;
  selecting: boolean;
  onBack: (() => void) | null;
  onCampaign: ((campaignId: string) => void) | null;
}) {
  const { thread } = props;
  const row = thread.conversation;
  const [log, setLog] = useState(false);
  const logId = useId();
  const assignment = props.actions.assign?.assignment ?? null;
  const snoozed = snoozedUntil(thread);
  const subject = rowSubject(row.subject, row.campaign.name, true).trim();
  return (
    <header className="lcr-head lcr-i2-head">
      <h2 className="lcr-i2-head-subject">{subject || "(no subject)"}</h2>
      <div className="lcr-i2-head-bar">
        {props.onBack && (
          // Its mark alone, so Done keeps its word on a phone's one row.
          <button
            type="button"
            className="lcr-back lcr-small-button lcr-i2-head-back"
            aria-label="Back to inbox"
            onClick={props.onBack}
          >
            <BackIcon size={16} />
          </button>
        )}
        <HeadActions
          thread={thread}
          actions={props.actions}
          showKeys={props.showKeys}
          selecting={props.selecting}
        />
      </div>
      <p className="lcr-head-meta lcr-i2-head-meta">
        <span className="lcr-i2-head-lead">{personName(row)}</span>
        {row.company && <span className="lcr-i2-head-company">{row.company}</span>}
        {/* Each in a span of its own: a meta item's lead holds the "·", outside the marker's edge. */}
        {row.meetingRequest === true && (
          <span className="lcr-i2-head-marker">
            <MeetingRequestMarker note={meetingNote(thread)} />
          </span>
        )}
        {row.dataRequest === true && (
          <span className="lcr-i2-head-marker">
            <DataRequestMarker />
          </span>
        )}
        <HeadCampaign campaign={row.campaign} onCampaign={props.onCampaign} />
        {thread.mailbox && <span className="lcr-i2-head-mailbox">{thread.mailbox}</span>}
        {snoozed && (
          <span className="lcr-i2-head-snoozed">
            <ClockIcon size={14} />
            <span>
              Snoozed until <time dateTime={snoozed}>{appWeekdayTime(snoozed)}</time>
            </span>
          </span>
        )}
        {thread.bisonUrl && (
          <span className="lcr-i2-head-bison-item lcr-i2-meta-tipped">
            <a
              className="lcr-i2-head-bison"
              href={thread.bisonUrl}
              target="_blank"
              rel="noreferrer"
              aria-label="Open in Bison, opens a new tab"
            >
              <ExternalLinkIcon size={14} />
              <span className="lcr-i2-head-bison-word">Open in Bison</span>
            </a>
            <Tip className="lcr-i2-meta-tip lcr-i2-narrow-tip">Open in Bison</Tip>
          </span>
        )}
        <HeadHistory thread={thread} open={log} panelId={logId} onToggle={() => setLog(!log)} />
      </p>
      {log && (
        <ChangeLog thread={thread} id={logId}>
          <HandoverMeta thread={thread} assignment={assignment} />
          {thread.seenBy && thread.seenBy.length > 0 && (
            <p className="lcr-i2-seen-by">
              Seen by{" "}
              {thread.seenBy.map((person, index) => (
                <span key={person.userId}>
                  {index > 0 ? "; " : ""}
                  {person.name}, <time dateTime={person.at}>{appDateTime(person.at)}</time>
                </span>
              ))}
            </p>
          )}
        </ChangeLog>
      )}
    </header>
  );
}

/** The pane's scrollbar, as a width the stylesheet takes out of the right gutter. */
function useScrollbarRoom(pane: React.RefObject<HTMLDivElement | null>) {
  useLayoutEffect(() => {
    const element = pane.current;
    if (element)
      element.style.setProperty(
        "--lcr-thread-bar",
        `${element.offsetWidth - element.clientWidth}px`,
      );
  }, [pane]);
}

export function ThreadView(props: {
  workspaceId: string;
  thread: InboxThread;
  showKeys: boolean;
  /** Rows are ticked in the list, so the inbox's action keys are theirs (`HeadActions`). */
  selecting?: boolean;
  relabelOpen: boolean;
  onBack: (() => void) | null;
  onRelabelOpen: (open: boolean) => void;
  onRelabel: (label: InboxLabelCode | null) => void;
  /** More's menu, held by the page so "." opens it; without them the head holds it itself. */
  moreOpen?: boolean;
  onMoreOpen?: (open: boolean) => void;
  onConfirm: () => void;
  onDone: () => void;
  /** The rep's word on the meeting flag (LEA-494); absent, More offers no meeting row. */
  onMeeting?: (meeting: boolean) => void;
  /** Delete into Trash, or Restore one in it (LEA-457); absent, the head has neither. */
  onTrash?: () => void;
  /** Opened from Trash: its action restores. */
  inTrash?: boolean;
  onSeen: (seen: boolean) => void;
  /** Snooze from the head; absent, the head has no Snooze. */
  onSnoozeOpen?: () => void;
  snoozeMenu?: ReactNode;
  /** Assign from the head, where assignment is on; absent, the head has no Assign. */
  assign?: AssignControl | null;
  /** The campaign link: the list narrowed to that campaign. Absent: the name is plain text. */
  onCampaign?: (campaignId: string) => void;
  attachmentHref: (id: string) => string;
  /** Step 8's mount point: the agent's material, under the reply box, above the newest message. */
  afterNewest?: ReactNode;
  /** The reply box, first in the scroll under the head (`reply-box.tsx`, LEA-514); absent, none. */
  reply?: ReactNode;
  replyAction?: (message: InboxMessage) => ReactNode;
  /** Each message's own boxes, drawn above its card (`MessageBoxes`, LEA-514). */
  boxAbove?: (message: InboxMessage) => ReactNode;
}) {
  const { thread } = props;
  const row = thread.conversation;
  const send = useReplySend(props.workspaceId, row.id, row.leadMessageId);
  const pane = useRef<HTMLDivElement>(null);
  useViewHeld(pane, row.id);
  useScrollbarRoom(pane);
  // Drawn once its emails have their height, so they do not push the newest out of view (W6).
  const settle = usePaneSettle(pane, row.id);
  const [ownMore, setOwnMore] = useState(false);
  const actions: ThreadActions = {
    relabelOpen: props.relabelOpen,
    onRelabelOpen: props.onRelabelOpen,
    onRelabel: props.onRelabel,
    onConfirm: props.onConfirm,
    onDone: props.onDone,
    onTrash: props.onTrash ?? null,
    inTrash: props.inTrash === true,
    onSeen: props.onSeen,
    onSnoozeOpen: props.onSnoozeOpen ?? null,
    snoozeMenu: props.snoozeMenu ?? null,
    assign: props.assign ?? null,
    moreOpen: props.moreOpen ?? ownMore,
    onMoreOpen: props.onMoreOpen ?? setOwnMore,
    onMeeting: props.onMeeting ?? null,
  };
  return (
    <article className="lcr-i2-conversation" aria-label={`Conversation with ${personName(row)}`}>
      <ThreadHead
        thread={thread}
        actions={actions}
        showKeys={props.showKeys}
        selecting={props.selecting === true}
        onBack={props.onBack}
        onCampaign={props.onCampaign ?? null}
      />
      {/* Newest first: the box, the agent's item, then the messages, the newest on top (LEA-514). */}
      <div className="lcr-thread-pane lcr-i2-thread" ref={pane}>
        {props.reply}
        {props.afterNewest && <div className="lcr-i2-after-newest">{props.afterNewest}</div>}
        <FrameSettleProvider value={settle}>
          <Messages
            key={row.id}
            thread={thread}
            workspaceId={props.workspaceId}
            attachmentHref={props.attachmentHref}
            forwardEnabled={
              !!send.status && thread.messages.some((message) => message.direction === "inbound")
            }
            {...(props.replyAction ? { replyAction: props.replyAction } : {})}
            {...(props.boxAbove ? { boxAbove: props.boxAbove } : {})}
          />
        </FrameSettleProvider>
      </div>
    </article>
  );
}
